import React, { useContext, useEffect } from 'react'

import { useFlag, useUserSignedIn } from '@sketch/modules-common'

import { Dropdown, ModalContext } from '@sketch/components'
import DownloadOptions from './DownloadOptions'
import RenameAction from './RenameAction'
import CopyLink from './CopyLink'
import ManageProjectAndCollectionActions from './ManageProjectAndCollectionActions'
import { DeleteShareModal } from '../DeleteShareModal'
import LeaveShareModal from '../LeaveShareModal'
import { ExportDesignTokensModal } from 'modules/shares/modals/ExportDesignTokensModal'
import { DuplicateShareOption } from '../DuplicateShareOption/DuplicateShareOption'
import { getEligibleWorkspacesForDuplication } from 'modules/workspace/utils/getEligibleWorkspacesForDuplication/getEligibleWorkspacesForDuplication'

import {
  GetShareQueryVariables,
  ShareListItemFragment,
  GetShareDocument,
  useGetWorkspacesForDuplicationQuery,
  usePinShareMutation,
  useUnpinShareMutation,
  GetShareDropdownOptionsDocument,
  GetShareDropdownOptionsQueryVariables,
} from '@sketch/gql-types'

import { useSharingModal } from 'modules/shares/hooks/useSharingModal'
import { useDocumentInfoModal } from 'modules/shares/hooks/useDocumentInfoModal'
import { useApolloClient } from '@apollo/react-hooks'
import {
  DropdownOptions,
  useGetShareDropdownOptions,
} from 'modules/shares/operations/useGetShareDropdownOptions'
import { useHideAnnotationDots } from 'modules/annotations/hooks'
import { useCheckCurrentAuthorizationForWorkspace } from 'modules/sso/operations/useCheckCurrentAuthorizationForWorkspace'

export interface ShareForShareDropdown
  extends Pick<
    ShareListItemFragment,
    | 'identifier'
    | 'name'
    | 'project'
    | 'userCanUpdateSettings'
    | 'publicAccessLevel'
    | 'type'
    | 'pinnedByCurrentUserAt'
  > {}

export interface VersionsForDropdown {
  current: { shortId: string }
  latest: { shortId: string }
}

export interface ShareActionsDropdownProps {
  share: ShareForShareDropdown
  workspaceIdentifier: string
  isViewingLatestVersion?: boolean
  versions: VersionsForDropdown
  onDuplicateShareClick: () => void
}

type PinDocumentProps = {
  pinned?: string | null
  shareIdentifier: string
  versionShortId: string
}

const typeInline = <T extends any>(variables: T) => variables

// This is just to preload the data if it wasn't loaded already
// It should give faster rendering speeds when any of the options is chosen
const usePrefetchGetShare = (identifier: string) => {
  const client = useApolloClient()

  useEffect(() => {
    const variables: GetShareQueryVariables = {
      shortId: identifier,
    }
    client.query({
      query: GetShareDocument,
      variables,
      fetchPolicy: 'cache-first',
    })
  }, [client, identifier])
}

const HIDE_ANNOTATION_DOTS_COPY = {
  false: 'Hide Comments',
  true: 'Show Comments',
} as const

interface LoadingDropdownOptions extends ShareActionsDropdownProps {
  dropdownOptions: DropdownOptions
}

const DownloadOptionsLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props

  if (dropdownOptions.loading) return <Dropdown.Skeleton count={2} />
  if (dropdownOptions.error) return null

  const share = { ...props.share, ...dropdownOptions.data.share }

  return (
    <DownloadOptions
      document={dropdownOptions.data.document}
      share={share}
      download={dropdownOptions.data.download}
      isViewingLatestVersion={props.isViewingLatestVersion}
    />
  )
}

const LeaveShareLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props
  const { showModal } = useContext(ModalContext)

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  const share = { ...props.share, ...dropdownOptions.data.share }
  if (!share.userCanLeaveShare) return null

  return (
    <>
      <Dropdown.Divider />
      <Dropdown.Item
        intent="negative"
        onClick={() =>
          showModal(LeaveShareModal, {
            shareIdentifier: share.identifier!,
            shareName: share.name!,
          })
        }
      >
        Leave&hellip;
      </Dropdown.Item>
    </>
  )
}

const RenameShareLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions, share } = props

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  if (!dropdownOptions.data.share.userCanEdit) return null

  const shareIdentifier = share?.identifier
  const shareName = share?.name

  return (
    <>
      <Dropdown.Divider />
      <RenameAction id={shareIdentifier} name={shareName}>
        Rename&hellip;
      </RenameAction>
    </>
  )
}

const HideAnnotationsLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props

  const [hideAnnotations, setHideAnnotations] = useHideAnnotationDots() || []

  if (hideAnnotations === undefined) {
    return null
  }

  if (dropdownOptions.loading) return <Dropdown.Skeleton />
  if (dropdownOptions.error) return null

  if (!dropdownOptions.data.share.commentsEnabled) return null

  return (
    <>
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => setHideAnnotations?.(!hideAnnotations)}>
        {HIDE_ANNOTATION_DOTS_COPY[`${hideAnnotations}` as const]}
        <Dropdown.Item.ShortcutText>Shift N</Dropdown.Item.ShortcutText>
      </Dropdown.Item>
    </>
  )
}

const DuplicateShareLoading = (props: LoadingDropdownOptions) => {
  const { dropdownOptions } = props
  const {
    error: getWorkspacesError,
    loading: getWorkspacesLoading,
    data: getWorkspacesData,
  } = useGetWorkspacesForDuplicationQuery()

  const { checkAuthorization } = useCheckCurrentAuthorizationForWorkspace()

  if (dropdownOptions.loading || getWorkspacesLoading) {
    return <Dropdown.Skeleton />
  }

  if (dropdownOptions.error || getWorkspacesError || !getWorkspacesData) {
    return null
  }

  const workspaces = getWorkspacesData!.me.workspaces
  const userCanInspect = dropdownOptions.data.share.userCanInspect

  const {
    userCanDuplicate,
    currentWorkspace,
    otherWorkspaces,
  } = getEligibleWorkspacesForDuplication(
    workspaces,
    dropdownOptions.data.share.workspace.identifier,
    dropdownOptions.data.share.publicAccessLevel,
    { hasAccessToWorkspaceCheckFn: checkAuthorization }
  )

  const isLoggedInWithProperSSOCredentials = currentWorkspace
    ? checkAuthorization(currentWorkspace)
    : true

  if (
    !userCanInspect ||
    !userCanDuplicate ||
    !isLoggedInWithProperSSOCredentials ||
    !dropdownOptions.data.document.sketchVersion
  ) {
    return null
  }

  const sketchVersion = dropdownOptions.data.document.sketchVersion!

  return (
    <DuplicateShareOption
      sketchVersion={sketchVersion}
      moreThanOneEligible={!currentWorkspace && Boolean(otherWorkspaces.length)}
      onClick={props.onDuplicateShareClick}
    />
  )
}

const PinDocument = ({
  pinned,
  shareIdentifier,
  versionShortId,
}: PinDocumentProps) => {
  const [pinShare] = usePinShareMutation({
    variables: {
      shareIdentifier,
    },
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetShareDropdownOptionsDocument,
        variables: typeInline<GetShareDropdownOptionsQueryVariables>({
          shareIdentifier,
          versionShortId,
        }),
      },
    ],
  })

  const [unpinShare] = useUnpinShareMutation({
    variables: {
      shareIdentifier,
    },
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetShareDropdownOptionsDocument,
        variables: typeInline<GetShareDropdownOptionsQueryVariables>({
          shareIdentifier,
          versionShortId,
        }),
      },
    ],
  })

  if (pinned) {
    return (
      <Dropdown.Item
        onClick={() => unpinShare({ variables: { shareIdentifier } })}
      >
        Unpin Document
      </Dropdown.Item>
    )
  }

  return (
    <Dropdown.Item onClick={() => pinShare({ variables: { shareIdentifier } })}>
      Pin Document
    </Dropdown.Item>
  )
}

export const ShareActionsDropdown: React.FC<ShareActionsDropdownProps> = props => {
  const { share, versions, workspaceIdentifier } = props
  const isExportDesignTokensEnabled = useFlag('export-design-tokens')
  const isPinningEnabled = useFlag('pinning')
  const isSignedIn = useUserSignedIn()
  const dropdownOptions = useGetShareDropdownOptions({
    share,
    version: versions.current,
  })
  usePrefetchGetShare(share.identifier)

  const { showModal } = useContext(ModalContext)
  const { openShareSettingsModal } = useSharingModal(share)
  const { openDocumentInfoModal } = useDocumentInfoModal({ share, versions })

  const shareIdentifier = share?.identifier
  const shareName = share?.name
  const pinned = dropdownOptions.data?.share.pinnedByCurrentUserAt
  const versionShortId = versions.current.shortId

  if (!share.userCanUpdateSettings) {
    return (
      <>
        <Dropdown.Header>Document</Dropdown.Header>
        <DownloadOptionsLoading {...props} dropdownOptions={dropdownOptions} />
        {isSignedIn && (
          <DuplicateShareLoading {...props} dropdownOptions={dropdownOptions} />
        )}
        {isPinningEnabled && (
          <PinDocument
            pinned={pinned}
            shareIdentifier={shareIdentifier}
            versionShortId={versionShortId}
          />
        )}
        <Dropdown.Divider />
        <CopyLink shareIdentifier={share.identifier} />
        <RenameShareLoading {...props} dropdownOptions={dropdownOptions} />
        <HideAnnotationsLoading {...props} dropdownOptions={dropdownOptions} />
        <LeaveShareLoading {...props} dropdownOptions={dropdownOptions} />
      </>
    )
  }

  return (
    <>
      <Dropdown.Header>Document</Dropdown.Header>
      <DownloadOptionsLoading {...props} dropdownOptions={dropdownOptions} />
      {isSignedIn && (
        <DuplicateShareLoading {...props} dropdownOptions={dropdownOptions} />
      )}
      {isPinningEnabled && (
        <PinDocument
          pinned={pinned}
          shareIdentifier={shareIdentifier}
          versionShortId={versionShortId}
        />
      )}
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => openDocumentInfoModal()}>
        Settings&hellip;
      </Dropdown.Item>
      <Dropdown.Item onClick={() => openShareSettingsModal()}>
        Share&hellip;
      </Dropdown.Item>
      <CopyLink shareIdentifier={share.identifier} />
      {isExportDesignTokensEnabled && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() =>
              showModal(ExportDesignTokensModal, {
                shareIdentifier: share.identifier!,
                versionShortId: versions.current.shortId!,
                userCanEditExportUrl: share.userCanUpdateSettings,
              })
            }
          >
            Export Design Tokens&hellip;
          </Dropdown.Item>
        </>
      )}
      <Dropdown.Divider />
      <RenameAction id={shareIdentifier} name={shareName}>
        Rename&hellip;
      </RenameAction>
      <ManageProjectAndCollectionActions
        share={share}
        workspaceIdentifier={workspaceIdentifier}
      />
      <HideAnnotationsLoading {...props} dropdownOptions={dropdownOptions} />
      <Dropdown.Divider />
      <Dropdown.Item
        intent="negative"
        onClick={() =>
          showModal(DeleteShareModal, {
            name: shareName,
            shareShortId: shareIdentifier,
          })
        }
      >
        Delete&hellip;
      </Dropdown.Item>
    </>
  )
}
